import { Component, EventEmitter, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Actions, Select, Store } from '@ngxs/store';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { SortAllUsers } from '../../../../all-users/actions/index';
import { FeatureService } from '../../../../feature/services/feature.service';
import { MatStepper } from '@angular/material/stepper';
import { CourseOfferingExecutionsService } from 'app/library/course-offering-executions/services/course-offering-executions.service';
import { CourseRelevantExecution } from 'app/library/relevant-execution/models/course-relevant-execution.model';
import { TranslateService } from '@ngx-translate/core';
import { ModuleParticipantsDataSource } from 'app/library/module-participants/components/list/datasource';
import { ModuleParticipantsService } from 'app/library/module-participants/services/module-participants.service';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { ModuleParticipantsState } from 'app/library/module-participants/models/module-participants.state';
import { CourseRelevantExecutionsState } from 'app/library/relevant-execution/models/course-relevant-executions.state';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { CourseRelevantExecutionDisabledReasonEnum } from 'app/library/relevant-execution/enums/course-relevant-execution-disabled-reason.enum';

@Component({
    selector: 'app-add-bookings-dialog',
    templateUrl: './add-bookings-dialog.component.html',
    styleUrls: ['./add-bookings-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AddBookingsDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    private static readonly BOOKING_CONTEXT = 'booking';

    @Select(CourseRelevantExecutionsState.getFilteredEntities)
    executions$: Observable<CourseRelevantExecution[]>;
    executions: CourseRelevantExecution[] = [];

    @Input()
    entitie$: Observable<ModuleUser[]>;

    @Input()
    entityId: string;

    @Input()
    moduleId: string;

    @Input()
    title: string;

    @Input()
    role: string;

    @Input()
    selectedUsersList: User[] = [];

    @Input()
    showFirstStep: boolean = false;

    get shouldShowNextButton(): boolean {
        if (!this.stepper) {
            return false;
        }

        if (this.showFirstStep) {
            return this.stepper.selectedIndex === 0 || this.stepper.selectedIndex === 1;
        }

        return this.stepper.selectedIndex === 0;
    }

    get isLastStep(): boolean {
        return this.stepper?.steps && this?.stepper.selectedIndex === this?.stepper.steps.length - 1;
    }

    get canGoToNotificationStep(): boolean {
        return !!this.selectedExecution.value;
    }

    @ViewChild(MatStepper) stepper: MatStepper;

    onConfirm = new EventEmitter<User>();

    filters: {
        name: string;
        translationKey: string;
        isUserAttribute: boolean;
    }[] = [
        { name: 'module', translationKey: 'MODULE.SINGULAR', isUserAttribute: false },
        { name: 'userRole', translationKey: 'GENERAL.LABEL.USER_ROLE', isUserAttribute: false },
    ];

    notifyMembers = true;
    dataSource: ModuleParticipantsDataSource;
    displayedColumns = [];
    removable = true;
    searchInput = new FormControl();
    selectedExecution = new FormControl();
    hideEmail: boolean;
    roleLabel: string;
    isCourseCatalogFeatureEnabled: boolean;

    @Select(ModuleParticipantsState.getTotalCount)
    totalCount$: Observable<number>;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    constructor(
        private _store: Store,
        public dialogRef: MatDialogRef<AddBookingsDialogComponent>,
        private _action$: Actions,
        private _featureService: FeatureService,
        private _courseOfferingExecutionsService: CourseOfferingExecutionsService,
        private _moduleParticipantsService: ModuleParticipantsService,
        private _translateService: TranslateService
    ) {
        super();

        this.hideEmail = this._featureService.checkFeatureStatus(FeatureSwitchName.ParticipantEmailVisibility, 'inactive');
        this.isCourseCatalogFeatureEnabled = this._featureService.checkFeatureStatus(FeatureSwitchName.CourseCatalog);
    }

    async ngOnInit(): Promise<void> {
        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe((searchText) => {
            this.loadUsersPage(true);
        });

        if (this.entityId) {
            this._courseOfferingExecutionsService.fetchProgramExecutionCourseOfferingExecutions(
                this.moduleId,
                this.entityId,
                1,
                '',
                100,
                1,
                ModuleStatuses.visibleKeys.filter((status) => status !== 'planned'),
                this.getExecutionsContext()
            );
        }

        this.executions$
            .pipe(
                filter((executions) => !!executions),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((courseRelevantExecutions) => {
                this.executions = courseRelevantExecutions;

                this.findAndSetDefaultExecution(courseRelevantExecutions);
            });

        if (this.showFirstStep) {
            if (this.selectedUsersList.length) {
                this.dataSource = new ModuleParticipantsDataSource(
                    this.entitie$.pipe(
                        map((users) => {
                            return users?.map((item) => {
                                if (this.selectedUsersList.find((item2) => item2.id === item.id)) {
                                    item = Object.assign(new User(), item, {
                                        selected: true,
                                    });
                                }
                                return item;
                            });
                        })
                    ),
                    this._moduleParticipantsService
                );
            } else {
                this.dataSource = new ModuleParticipantsDataSource(this.entitie$, this._moduleParticipantsService);
            }

            setTimeout(() => {
                this.loadUsersPage(false);

                this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                    this._store.dispatch(new SortAllUsers(sort));

                    this.loadUsersPage(true);
                });

                this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                    this.loadUsersPage(false);
                });
            });
        }

        if (this.hideEmail) {
            this.displayedColumns = ['avatar', 'firstName', 'lastName', 'buttons'];
        } else {
            this.displayedColumns = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
        }
    }

    findAndSetDefaultExecution(courseRelevantExecutions: CourseRelevantExecution[]): void {
        const defaultExecution = courseRelevantExecutions.find((execution) => execution.isDefault && this.isExecutionSelectable(execution));

        if (defaultExecution) {
            this.selectedExecution.setValue(defaultExecution);
        }
    }

    isExecutionSelectable(execution: CourseRelevantExecution | null): boolean {
        return execution?.selectionStatus?.isSelectable ?? true;
    }

    getDisabledExecutionSuffixLabel(execution: CourseRelevantExecution | null): string {
        if (this.isExecutionSelectable(execution)) {
            return '';
        }

        if (execution.selectionStatus?.disabledReason === CourseRelevantExecutionDisabledReasonEnum.RegistrationClosed) {
            return 'PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.EXECUTION_SUFFIX_CLOSED';
        }

        if (execution.selectionStatus?.disabledReason === CourseRelevantExecutionDisabledReasonEnum.ExecutionPlanned) {
            return 'PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.EXECUTION_SUFFIX_PLANNED';
        }

        if (execution.selectionStatus?.disabledReason === CourseRelevantExecutionDisabledReasonEnum.CourseListed) {
            return 'PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.EXECUTION_SUFFIX_CATALOG_BOOKING_REQUIRED';
        }

        return '';
    }

    goForward(stepper: MatStepper): void {
        if (this.showFirstStep && stepper.selectedIndex === 0 && !this.selectedUsersList.length) {
            return;
        }

        if ((this.showFirstStep && stepper.selectedIndex === 1) || (!this.showFirstStep && stepper.selectedIndex === 0)) {
            if (!this.canGoToNotificationStep) {
                return;
            }
        }

        stepper.next();
    }

    async onStepChange(stepper: MatStepper): Promise<void> {
        const currentIndex = this.showFirstStep ? stepper.selectedIndex : stepper.selectedIndex + 1;

        if (currentIndex === 0) {
            this.title = this._translateService.instant('PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.CHOOSE_PARTICIPANTS');
        } else if (currentIndex === 1) {
            this.title = this._translateService.instant('PROGRAM_OFFERING.BUTTON.BOOK_INTO_EXECUTIONS');
        } else if (currentIndex === 2) {
            this.title = this._translateService.instant('GENERAL.LABEL.NOTIFICATION');
        }
    }

    removeFromList(user: User): void {
        this.selectedUsersList = [...this.selectedUsersList.filter((item) => item.id !== user.id)];

        this.dataSource = new ModuleParticipantsDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((moduleUser) => {
                        if (moduleUser.userId === user.id) {
                            moduleUser = Object.assign(new ModuleUser(), moduleUser, {
                                selected: false,
                            });
                        } else if (this.selectedUsersList.find((user) => user.id === moduleUser.userId)) {
                            moduleUser = Object.assign(new ModuleUser(), moduleUser, {
                                selected: true,
                            });
                        }
                        return moduleUser;
                    });
                })
            ),
            this._moduleParticipantsService
        );
    }

    addToList(user: User): void {
        this.selectedUsersList = [...this.selectedUsersList, user];
        this.dataSource = new ModuleParticipantsDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((moduleUser) => {
                        if (moduleUser.userId === user.id || this.selectedUsersList.find((user) => user.id === moduleUser.userId)) {
                            moduleUser = Object.assign(new ModuleUser(), moduleUser, {
                                selected: true,
                            });
                        }
                        return moduleUser;
                    });
                })
            ),
            this._moduleParticipantsService
        );
    }

    saveSelected(): void {
        this.dialogRef.close({ list: this.selectedUsersList, selectedExecution: this.selectedExecution.value, notify: this.notifyMembers });
    }

    confirm(user: User): void {
        this.onConfirm.next(user);
    }

    async loadUsersPage(resetPageIndex = false): Promise<void> {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        if (this.dataSource) {
            this.dataSource.loadUsers(this.entityId, this.searchInput.value, this.sort.active, this.sort.direction, this.paginator.pageSize, this.paginator.pageIndex + 1);
        }
    }

    private getExecutionsContext(): string {
        return this.isCourseCatalogFeatureEnabled ? AddBookingsDialogComponent.BOOKING_CONTEXT : null;
    }
}
